import type { ReactNode } from 'react';
import { Size, type BannerPill } from '@bms2/shared';
import { Pill } from '../../components/controls';
import styles from './BannerPills.module.css';

/**
 * 🔒 One element draws a banner's pills (T176). The product owner, 2026-09-22:
 * *"How many custom one off items are there that should be a function like page
 * title is this a function that is used over or custom for each page"*.
 *
 * 💰 Eight pages carried the same `pills.map` — the same `toUpperCase`, the same
 * two `var(--status-…)` lookups, the same wrapper class — each in its own file
 * and each free to drift. A pill's size was the one thing they disagreed on, and
 * it is a prop here rather than eight copies of a choice.
 *
 * 🔨 The status pills are Medium and the record's own small pills under the name
 * are Small; that is the whole difference and there is no third size.
 */
export function BannerPills({ pills, size = Size.Medium, attribute, hint = null, children }: {
  pills: readonly BannerPill[];
  size?: Size.Small | Size.Medium;
  /** The attribute a suite reads the group by, such as `data-bill-pills`, spelled as each page has always spelled it. */
  attribute: string;
  /** 🔒 The approval's full sentence, read on the pills' hover (T739; decision 2482); null draws no hover. */
  hint?: string | null;
  /** Anything drawn inside the same group beside the pills, such as the record page's own clearable flags. */
  children?: ReactNode;
}) {
  return (
    <span className={styles.pills} {...{ [attribute]: 'true' }} {...(hint ? { title: hint, 'data-pills-hint': 'approval' } : {})}>
      {pills.map((pill) => (
        <Pill
          key={pill.text}
          text={pill.text}
          foreground={`var(--status-${pill.group}-text)`}
          background={`var(--status-${pill.group}-bg)`}
          size={size}
        />
      ))}
      {children}
    </span>
  );
}
